// src/pages/BrandStories.js
import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  Stack,
  Link as MLink,
} from "@mui/material";

import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LanguageRoundedIcon from "@mui/icons-material/LanguageRounded";
import { getPrimaryColor } from "../../../GlobalFunction/Theme";
import { BrandsAPI } from "../../../../api/Brands";
import { getLangFromCookie } from "../../../../Utils/LangCookie";
import SehoLoadingSpinner from "../../../GlobalFunction/SehoLoadingSpinner";

/* --------------------------------------------------------
   TRANSLATIONS
--------------------------------------------------------- */
const TEXT = {
  title: {
    ZH: "品牌介紹",
    EN: "Brands",
    JP: "ブランド紹介",
  },
  searchPlaceholder: {
    ZH: "搜尋品牌",
    EN: "Search brands",
    JP: "ブランドを検索",
  },
  website: {
    ZH: "網站",
    EN: "Website",
    JP: "公式サイト",
  },
  noResult: {
    ZH: "未找到匹配品牌。",
    EN: "No matching brands found.",
    JP: "該当するブランドが見つかりません。",
  },
  descriptionFallback: {
    ZH: "（尚無品牌說明）",
    EN: "(No brand description available)",
    JP: "（ブランド説明は準備中です）",
  },
};

/* Priority order (case-insensitive) */
const PRIORITY = ["電潔水", "擦拭君", "seho", "mizunogi", "dyson", "saraya"];

function normKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

/**
 * ✅ Build WP origin WITHOUT frontend port (3000)
 * Priority:
 *  1) REACT_APP_WP_ORIGIN
 *  2) REACT_APP_WP_GRAPHQL_URL (strip /graphql)
 *  3) current page protocol + hostname (NO port) + /wp-headless
 */
function getWpOriginNoPort() {
  const rawEnv =
    process.env.REACT_APP_WP_ORIGIN ||
    process.env.REACT_APP_WP_GRAPHQL_URL?.replace(/\/graphql\/?$/, "");

  if (rawEnv) return String(rawEnv).replace(/\/+$/, "");

  if (typeof window !== "undefined" && window.location) {
    const hostNoPort = `${window.location.protocol}//${window.location.hostname}`;
    return `${hostNoPort}/wp-headless`;
  }

  return "http://localhost/wp-headless";
}

function safeDecodeURIComponent(s) {
  try {
    return decodeURIComponent(s);
  } catch {
    return s;
  }
}

/**
 * Minimal logo URL resolver (page-side only)
 * - does NOT duplicate BrandsAPI REST logic
 * - does NOT include :3000
 */
function toLogoUrl(raw) {
  if (!raw) return "";

  const s0 = String(raw).replace(/\\/g, "/").trim();
  if (!s0) return "";
  if (/^(data:|blob:)/i.test(s0)) return s0;

  // already absolute
  if (/^https?:\/\//i.test(s0)) return s0;

  const wpOrigin = getWpOriginNoPort(); // never includes :3000

  // Most correct server path:
  // - /wp-content/uploads/...
  // - /wp-headless/wp-content/uploads/...
  // - /uploads/... (legacy alias)
  if (s0.startsWith("/uploads/")) {
    return `${wpOrigin}${"/wp-content"}${s0}`;
  }

  if (s0.startsWith("/")) {
    // If wpOrigin already contains "/wp-headless", joining is safe
    return `${wpOrigin}${s0}`;
  }

  // relative fallback
  return `${wpOrigin}/wp-content/uploads/${s0}`;
}

export default function BrandStories({ mode }) {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const PRIMARY = getPrimaryColor(mode);

  const [lang, setLang] = useState("ZH");

  /* -------------------- Listen to LangCookie -------------------- */
  useEffect(() => {
    const syncLang = () => {
      const c = getLangFromCookie();
      setLang(["ZH", "EN", "JP"].includes(c) ? c : "ZH");
    };

    syncLang();
    window.addEventListener("app:languageChanged", syncLang);
    return () => window.removeEventListener("app:languageChanged", syncLang);
  }, []);

  /* -------------------- Load Brands (WP) -------------------- */
  useEffect(() => {
    let alive = true;

    async function loadBrands() {
      try {
        const list = await BrandsAPI.list({ lang });
        if (!alive) return;

        const allowed = new Set(
          ["saraya", "seho", "mizunogi", "dyson", "電潔水", "擦拭君"].map(
            normKey,
          ),
        );

        let mapped = (Array.isArray(list) ? list : [])
          .map((b) => {
            const idRaw = String(b?.brandID ?? "").trim();
            const idDecoded = safeDecodeURIComponent(idRaw);
            const idNorm = normKey(idDecoded || idRaw);

            return {
              id: idDecoded || idRaw,
              __idNorm: idNorm,

              companyName: b?.brandName || "",
              descZH: b?.brandDescZH || "",
              descEN: b?.brandDescEN || "",
              descJP: b?.brandDescJP || "",
              website: b?.brandLink || "",
              logo: b?.brandLogo || b?.logo || b?.image || "",
            };
          })
          .filter((b) => allowed.has(b.__idNorm));

        const priorityNorm = PRIORITY.map(normKey);
        mapped.sort((a, b) => {
          const aI = priorityNorm.indexOf(a.__idNorm);
          const bI = priorityNorm.indexOf(b.__idNorm);
          return (aI === -1 ? 999 : aI) - (bI === -1 ? 999 : bI);
        });

        setRows(mapped);
      } catch (e) {
        console.error("Failed to load brands:", e);
        if (!alive) return;
        setRows([]);
      }
    }

    loadBrands();
    return () => {
      alive = false;
    };
  }, [lang]);

  /* -------------------- Search -------------------- */
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) =>
      [r.companyName, r.website, r.id].join(" ").toLowerCase().includes(q),
    );
  }, [rows, query]);

  return (
    <Box sx={{ bgcolor: "#fffffc", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="md">
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 3, textAlign: "center", color: PRIMARY }}
        >
          {TEXT.title[lang]}
        </Typography>

        {/* SEARCH BAR */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
          <Box
            sx={{
              display: "flex",
              maxWidth: 520,
              width: "100%",
              bgcolor: "#fff",
              borderRadius: 999,
              overflow: "hidden",
              boxShadow: "0 0 0 1px rgba(0,0,0,0.1)",
            }}
          >
            <TextField
              variant="standard"
              placeholder={TEXT.searchPlaceholder[lang]}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              InputProps={{
                disableUnderline: true,
                sx: { px: 2, py: 1.2, fontSize: 14 },
              }}
              fullWidth
            />
            <Box
              sx={{
                width: 56,
                bgcolor: PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SearchRoundedIcon sx={{ color: "#fff" }} />
            </Box>
          </Box>
        </Box>

        {/* BRAND CARDS */}
        {filtered.map((row) => (
          <BrandCard
            key={row.id || row.companyName}
            row={row}
            lang={lang}
            PRIMARY={PRIMARY}
          />
        ))}

        {filtered.length === 0 && (
          <Typography sx={{ mt: 6, textAlign: "center", color: "#777" }}>
            {TEXT.noResult[lang]}
          </Typography>
        )}
      </Container>
    </Box>
  );
}

/* --------------------------------------------------------
   BRAND CARD
--------------------------------------------------------- */
function BrandCard({ row, lang, PRIMARY }) {
  const fontType = "LINE Sans TW";
  const { companyName, descZH, descEN, descJP, website, logo } = row;

  const logoSrc = logo ? toLogoUrl(logo) : "";
  const [logoReady, setLogoReady] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  useEffect(() => {
    setLogoReady(false);
    setLogoFailed(false);
  }, [logoSrc]);

  const handleLogoLoad = async (event) => {
    const imageElement = event.currentTarget;

    try {
      if (typeof imageElement.decode === "function") {
        await imageElement.decode();
      }
    } catch {
      // The logo may still be displayable even when decode rejects.
    }

    setLogoFailed(false);
    setLogoReady(true);
  };

  const handleLogoError = () => {
    console.warn("Brand logo failed:", row?.id, logoSrc);
    setLogoFailed(true);
    setLogoReady(true);
  };

  let description = "";
  if (lang === "EN") description = descEN || descZH || descJP;
  else if (lang === "JP") description = descJP || descZH || descEN;
  else description = descZH || descEN || descJP;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 3,
        border: "1px solid #c0b9b9ff",
        overflow: "hidden",
        mb: 3,
      }}
    >
      <Box sx={{ bgcolor: PRIMARY, color: "#fff", height: 36 }} />

      <Box
        sx={{
          p: 3,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "center",
          bgcolor: "#fff",
          gap: 3,

          "@media (min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)":
            {
              p: 4,
              gap: 2.5,
            },
        }}
      >
        {/* LOGO */}
        <Box
          sx={{
            flexShrink: 0,
            width: 120,
            height: 120,
            borderRadius: 2,
            bgcolor: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            overflow: "hidden",
            position: "relative",
            fontWeight: 700,
            color: PRIMARY,

            "@media (max-width: 767.95px)": {
              width: 210,
              height: 145,
            },

            "@media (min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)":
              {
                width: 240,
                height: 150,
              },
          }}
        >
          {logoSrc && !logoReady && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 1,
                display: "grid",
                placeItems: "center",
                pointerEvents: "none",
              }}
            >
              <SehoLoadingSpinner />
            </Box>
          )}

          {logoSrc ? (
            <Box
              component="img"
              src={logoSrc}
              alt={companyName}
              loading="lazy"
              decoding="async"
              data-global-loading-ignore="true"
              onLoad={handleLogoLoad}
              onError={handleLogoError}
              sx={{
                maxWidth: "100%",
                maxHeight: "100%",
                objectFit: "contain",
                opacity: logoReady && !logoFailed ? 1 : 0,
                transition: "opacity .2s ease",

                "@media (max-width: 767.95px)": {
                  width: "100%",
                  height: "100%",
                },
              }}
            />
          ) : (
            "LOGO"
          )}
        </Box>

        {/* MAIN CONTENT */}
        <Box
          sx={{
            flexGrow: 1,
            minWidth: 0,

            "@media (min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)":
              {
                width: "100%",
                maxWidth: 680,
                mx: "auto",
                textAlign: "center",
              },
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: PRIMARY,
              fontWeight: 700,
              fontFamily: fontType,
              mb: 1,
              textTransform: "uppercase",

              "@media (min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)":
                {
                  mb: 1.5,
                  fontSize: "1.75rem",
                  lineHeight: 1.25,
                  textAlign: "center",
                },
            }}
          >
            {companyName}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              fontWeight: 400,
              fontFamily: fontType,
              mb: 2,
              whiteSpace: "pre-line",

              "@media (min-width: 768px) and (max-width: 899.95px) and (orientation: portrait)":
                {
                  fontSize: "1rem",
                  lineHeight: 1.75,
                  textAlign: "center",
                },
            }}
          >
            {description || TEXT.descriptionFallback[lang]}
          </Typography>
        </Box>

        {/* WEBSITE */}
        <Box
          sx={{
            minWidth: 130,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          {website && (
            <Stack direction="row" spacing={1} alignItems="center">
              <LanguageRoundedIcon sx={{ color: PRIMARY }} />
              <MLink
                href={website}
                target="_blank"
                underline="hover"
                rel="noreferrer"
              >
                {TEXT.website[lang]}
              </MLink>
            </Stack>
          )}
        </Box>
      </Box>
    </Paper>
  );
}
